import React, { useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { uploadClaimProof } from "../../../../lib/api/claim.api";
import {
  Upload,
  FileText,
  X,
  AlertCircle,
  CheckCircle,
  Info,
} from "lucide-react";
import { useAuth } from "../../../../Context/AuthContext";

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];
const ALLOWED_EXT_LABEL = "JPG, PNG, WEBP, HEIC, HEIF, PDF";
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

const PROOF_EXAMPLES = [
  "Business registration certificate",
  "Official government-issued business license",
  "Utility bill in the business name",
  "Recent tax filing with business name",
];

export default function ProofUploadPage() {
  const { claimId } = useParams<{ claimId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = (f: File): string | null => {
    if (!ALLOWED_TYPES.includes(f.type)) {
      return `File type not supported. Please upload: ${ALLOWED_EXT_LABEL}`;
    }
    if (f.size > MAX_SIZE_BYTES) {
      return "File is too large. Maximum size is 10 MB.";
    }
    return null;
  };

  const handleFileSelect = (f: File) => {
    const err = validateFile(f);
    if (err) {
      setFileError(err);
      setFile(null);
      return;
    }
    setFileError(null);
    setFile(f);
    setApiError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  };

  const handleRemove = () => {
    setFile(null);
    setFileError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!file || !claimId || loading) return;
    setLoading(true);
    setApiError(null);

    try {
      await uploadClaimProof(claimId, file, accessToken);
      setSubmitted(true);
    } catch (err: any) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatSize = (bytes: number) =>
    bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(1)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center font-['DM_Sans',sans-serif]">
        <div className="max-w-md w-full mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2
            className="text-2xl font-semibold text-[#1C1917] mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Proof submitted
          </h2>
          <p className="text-[#6B6460] text-sm leading-relaxed mb-8">
            Your claim is under manual review. This typically takes 1–3 business
            days. You can check the restaurant listing for status updates on your profile.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-left flex items-start gap-3">
            <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              The restaurant listing will show <strong>"Pending manual verification"</strong>{" "}
              until our team has reviewed your submission.
            </p>
          </div>
          <button
            onClick={() => navigate("/")}
            className="mt-6 w-full px-6 py-3.5 rounded-xl bg-[#C17B3F] hover:bg-[#A8652F] text-white text-sm font-semibold transition-colors"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7] font-['DM_Sans',sans-serif]">
      <div className="border-b border-[#E8E4DC] bg-white px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Upload className="w-5 h-5 text-[#C17B3F]" />
          <span className="text-sm font-medium text-[#6B6460] tracking-wide uppercase">
            Upload Verification Proof
          </span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="mb-10">
          <h1
            className="text-[2.25rem] font-semibold text-[#1C1917] leading-tight mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Upload supporting proof
          </h1>
          <p className="text-[#6B6460] text-base leading-relaxed">
            Since we couldn't verify your ownership via email, please provide a
            document confirming your connection to this business.
          </p>
        </div>

        {/* Examples */}
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5 mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#6B6460] mb-3">
            Accepted documents
          </p>
          <ul className="space-y-2">
            {PROOF_EXAMPLES.map((ex, i) => (
              <li key={i} className="flex items-center gap-2.5 text-sm text-[#44403C]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C17B3F] flex-shrink-0" />
                {ex}
              </li>
            ))}
          </ul>
        </div>

        {/* Drop zone */}
        {!file ? (
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 ${
              dragOver
                ? "border-[#C17B3F] bg-[#FDF5EC]"
                : "border-[#D1C9BE] bg-white hover:border-[#C17B3F]/50 hover:bg-[#FDF5EC]/50"
            }`}
          >
            <Upload className="w-8 h-8 text-[#C17B3F] mx-auto mb-3" />
            <p className="font-semibold text-[#1C1917] text-sm mb-1">
              Drop your file here, or{" "}
              <span className="text-[#C17B3F]">browse</span>
            </p>
            <p className="text-xs text-[#9B918B]">
              {ALLOWED_EXT_LABEL} · Max 10 MB
            </p>
            <input
              ref={inputRef}
              type="file"
              accept={ALLOWED_TYPES.join(",")}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileSelect(f);
              }}
            />
          </div>
        ) : (
          <div className="bg-white border-2 border-[#C17B3F]/30 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#FDF5EC] flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-[#C17B3F]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1C1917] truncate">
                {file.name}
              </p>
              <p className="text-xs text-[#9B918B]">{formatSize(file.size)}</p>
            </div>
            <button
              onClick={handleRemove}
              className="p-1.5 rounded-lg hover:bg-[#F5F0EB] text-[#9B918B] hover:text-[#1C1917] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* File validation error */}
        {fileError && (
          <div className="mt-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{fileError}</p>
          </div>
        )}

        {/* API error */}
        {apiError && (
          <div className="mt-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{apiError}</p>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!file || loading}
          className={`w-full mt-8 flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 ${
            file && !loading
              ? "bg-[#C17B3F] hover:bg-[#A8652F] text-white shadow-md hover:shadow-lg"
              : "bg-[#E8E4DC] text-[#B0A89E] cursor-not-allowed"
          }`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
              </svg>
              Uploading…
            </span>
          ) : (
            "Submit proof"
          )}
        </button>
      </div>
    </div>
  );
}